import { BrandMath, CurrencyMajor, CurrencyMinor, toCents, toDollars, ZERO_CENTS } from '@fox/atoms/src/inputs/currency-input';
import { ensureArray } from '@fox/atoms/utils/array';
import { roundNumber } from '@fox/atoms/utils/number-functions';
import { strIsNullOrEmpty, tryParseJSONObject } from '@fox/atoms/utils/string';
import { IWorkBenchLayout } from '@fox/blocks/contracts/work-bench';
import { BPointAddCreditCard } from '@fox/finance/src/components/add-payments/bpoint/add-credit';
import { BPointAddDirectDebit } from '@fox/finance/src/components/add-payments/bpoint/add-debit';
import { EWayAddCreditCard } from '@fox/finance/src/components/add-payments/eway/add-credit';
import { EziDebitAddCreditCard } from '@fox/finance/src/components/add-payments/ezidebit/add-credit';
import { EziDebitAddDirectDebit } from '@fox/finance/src/components/add-payments/ezidebit/add-debit';
import { FatZebraAddCreditCard } from '@fox/finance/src/components/add-payments/fat-zebra/add-credit';
import { NabTransactAddCreditCard } from '@fox/finance/src/components/add-payments/nab-transact/add-credit-card';
import { PayPalAddCreditCard } from '@fox/finance/src/components/add-payments/paypal/add-credit';
import { PayPalOneTimePayment } from '@fox/finance/src/components/add-payments/paypal/one-time-payment';
import { PayWayAddCreditCard } from '@fox/finance/src/components/add-payments/payway/add-credit';
import { StripeAddCreditCard } from '@fox/finance/src/components/add-payments/stripe/add-credit-card';
import { StripeOneTimePayment } from '@fox/finance/src/components/add-payments/stripe/one-time-payment';
import {
	AppliesToType,
	DiscountCodeModel,
	DiscountFragment,
	DiscountType,
	FormFinancialLineItem,
	PaymentMethod,
	StandardLineType
} from '@fox/finance/src/contracts';
import { PAYMENT_BLOCK_ANSWER_TYPE } from '../../../form-block-types';
import { IField, IQuestionAnswer } from '../../form-root/contracts';
import { ConditionResult } from '../form-block/conditionals/contracts';
import { checkQuestionConditionValid, findAllFormQuestionSelects } from '../form-block/conditionals/helpers';
import { ISection } from '../section-block/contracts';
import { calculateLineItemsTotal } from '../ticket-block/helpers';
import { FeeType, PaymentAnswerData, PaymentBlockFee, PaymentBlockMetadataType, TaxRatesData, ValidateDiscountResponse } from './contracts';

export const checkSuccessfulPayment = (answers: IQuestionAnswer[]) => {
	if (
		answers.some(
			a =>
				a.type === PAYMENT_BLOCK_ANSWER_TYPE &&
				tryParseJSONObject<{ successfulPaymentToken?: string }>(a.answer?.answerJson, {})?.successfulPaymentToken
		)
	) {
		return true;
	} else return false;
};

/**
 * Finds all possible data provided matching each requires item from supplied answers (excludes data from invalid conditionals)
 * @param form - The form object from context
 * @param requires - The array of required items
 * @returns A record of the required field with its array of providedData objects
 */
export const findRequiredData = (
	answers: IQuestionAnswer[],
	sections: ISection[],
	requires: PaymentBlockMetadataType['requires'],
	layout: IWorkBenchLayout | undefined
): Record<string, unknown> => {
	const toRet = {};
	for (const req of requires ?? []) {
		toRet[req] = answers
			.filter((a: IQuestionAnswer) => {
				const allBlocks = findAllFormQuestionSelects(sections);
				const lineTypeBlock = allBlocks.find(b => b.name === a.questionName);
				const conditionalDefinitions = lineTypeBlock?.metadataJson?.conditionalDefinitions;
				let conditionalValid: ConditionResult;
				if (conditionalDefinitions) {
					const definitionsArray = ensureArray(conditionalDefinitions);
					conditionalValid = definitionsArray.every(
						definition => checkQuestionConditionValid(sections, definition, answers, undefined, layout) === ConditionResult.Valid
					)
						? ConditionResult.Valid
						: ConditionResult.Invalid;
				} else {
					conditionalValid = ConditionResult.Valid;
				}
				return a.data?.[req] && conditionalValid === ConditionResult.Valid;
			})
			?.map(answer => answer?.data?.[req]);
	}
	return toRet;
};

/**
 * Finds the value of the first/last name and email for setup intent
 * Finds by the override id value first, then block with question id matching,
 * Then then uses the fallback of any contact block with matching value
 * @param fieldMeta
 * @param form - Entire form
 * @param questionName - Name of value to find
 * @returns The string value of the found required question name
 */
export function findBlockValue(input: { fieldMeta: PaymentBlockMetadataType; questionName: string; answers?: IQuestionAnswer[] }) {
	const { fieldMeta, questionName, answers } = input;
	if (!answers) return '';

	const allContactAnswers = answers.filter(a => a.type === 'CreatePerson');
	const allMultiContactAnswers = answers.filter(a => a.type === 'CreatePeople');
	const contactsFound = allContactAnswers.length > 0 ? allContactAnswers : allMultiContactAnswers;
	const contactWithAnswer = contactsFound?.find(a => {
		const parsed = tryParseJSONObject<any>(a.answer?.answerJson, {});
		return !strIsNullOrEmpty(parsed.contacts?.[0]?.[questionName]);
	});

	const matchingAnswer =
		answers.find(a => a.questionName === fieldMeta.userDetailIds?.[questionName] || a.questionName === questionName) ?? contactWithAnswer;

	if (matchingAnswer?.answer?.answerJson) {
		// TODO TYPE PEOPLE BLOCK ANSWER
		const parsed = tryParseJSONObject<any>(matchingAnswer.answer.answerJson, {});
		return parsed.contacts?.[0]?.[questionName];
	} else {
		return matchingAnswer?.answer?.answerString;
	}
}

export const PaymentComponents = {
	PayWay: {
		[PaymentMethod.CreditCard]: PayWayAddCreditCard
	},
	BPoint: {
		[PaymentMethod.CreditCard]: BPointAddCreditCard,
		[PaymentMethod.DirectDebit]: BPointAddDirectDebit
	},
	nab: {
		[PaymentMethod.CreditCard]: NabTransactAddCreditCard
	},
	FatZebra: {
		[PaymentMethod.CreditCard]: FatZebraAddCreditCard
	},
	Stripe: {
		[PaymentMethod.CreditCard]: StripeAddCreditCard,
		[PaymentMethod.OneTimePayment]: StripeOneTimePayment
	},
	PayPal: {
		[PaymentMethod.CreditCard]: PayPalAddCreditCard,
		[PaymentMethod.OneTimePayment]: PayPalOneTimePayment
	},
	EziDebit: {
		[PaymentMethod.CreditCard]: EziDebitAddCreditCard,
		[PaymentMethod.DirectDebit]: EziDebitAddDirectDebit
	},
	EWay: {
		[PaymentMethod.CreditCard]: EWayAddCreditCard
	}
};

/**
 * For visual UI only - Splits the financial line items into immediate and non-immediate payment categories
 * based on the selected payment method and existence of immediatePayment in lineItems.
 *
 * @param lineItemsData - The array of financial line items to process.
 * @param method - The selected payment method that determines if the line items are due immediately or later.
 * @returns An object containing two arrays:
 * - `lineItemsDataImmediate`: items requiring immediate payment (for CreditCard method).
 * - `lineItemsDataNotImmediate`: items due later which is all items if credit card method not used.
 */
export const groupLineItemsByImmediacy = (lineItemsData: FormFinancialLineItem[], method: PaymentMethod | undefined) => {
	let lineItemsDataImmediate: FormFinancialLineItem[] = [];
	let lineItemsDataNotImmediate: FormFinancialLineItem[] = [];
	if (method === PaymentMethod.CreditCard || method === PaymentMethod.OneTimePayment) {
		lineItemsDataImmediate = lineItemsData.filter(l => l.immediatePayment);
		lineItemsDataNotImmediate = lineItemsData.filter(l => !l.immediatePayment);
	} else {
		lineItemsDataNotImmediate = lineItemsData;
	}

	return { lineItemsDataImmediate, lineItemsDataNotImmediate };
};

/**
 * Find applicable payment fees for the specified payment block taking into considering the provider/method condition of a fee
 * @param questionName - Payment block question name
 * @param sections - Form sections
 * @param answers - Form answers
 * @returns
 */
export const findPaymentFees = (questionName: IField['name'], sections: ISection[], answers: IQuestionAnswer[] | undefined) => {
	const toRet: PaymentBlockFee[] = [];
	const thisAnswer = answers?.find(a => a.questionName === questionName) as IQuestionAnswer<PaymentAnswerData>;
	const allPaymentFields = sections.flatMap(s => s.fields)?.filter(f => f.type === PAYMENT_BLOCK_ANSWER_TYPE);
	const matchingField = allPaymentFields.find(f => f.name === questionName);
	if (!matchingField) return [];

	const parsed = tryParseJSONObject<PaymentBlockMetadataType>(matchingField.metadataJson, {});
	for (const fee of parsed.fees ?? []) {
		const hasMatchingMethod = fee.appliesTo?.some(a => a.method === thisAnswer?.data?.method);

		// Check the condition provider is still existing in the possible providers
		// In case it had been removed but condition wasn't removed
		let conditionProviderExists = false;
		for (const provider of parsed.provider ?? []) {
			if (fee.appliesTo?.some(a => a.method === provider.method && a.provider === provider.provider)) {
				conditionProviderExists = true;
				break;
			}
		}
		if (fee.appliesTo?.some(a => a.method === 'STATEMENT' && parsed.invoice?.enabled)) {
			conditionProviderExists = true;
		}
		// If there is no appliesTo it means it applies to all
		// If there is a hasMatchingMethod it means our applies to matches the provider selected by user
		// If there is no thisAnswer.data.method it means the user doesn't need to select a provider as there is only one (no option to choose)
		// But only if the provider still exists in the valid providers for the block incase the provider was removed and the condition wasn't
		const appliesToValid = !fee.appliesTo?.length || ((hasMatchingMethod || !thisAnswer?.data?.method) && conditionProviderExists);
		if (fee.amount && fee.amount > 0 && appliesToValid) {
			toRet.push(fee);
		}
	}

	return toRet;
};

// Note: This is only shown during form entry and is properly set in SNF
const createDiscountComment = (discount: ValidateDiscountResponse) => {
	if (discount.discountType === DiscountType.Percentage) {
		return `Discount: ${discount.description}: (${discount.amount}%)`;
	} else return `Discount: ${discount.description}`;
};

export const buildDiscountLineItems = (
	questionName: IField['name'],
	answers: IQuestionAnswer[] | undefined,
	baseLineItemsDataImmediate: FormFinancialLineItem[],
	baseLineItemsData: FormFinancialLineItem[]
): FormFinancialLineItem[] => {
	const thisAnswer = answers?.find(a => a.questionName === questionName) as IQuestionAnswer<PaymentAnswerData>;
	const discounts = thisAnswer?.data?.discounts;
	const isImmediate = baseLineItemsDataImmediate?.length > 0;

	const lineItems: FormFinancialLineItem[] = (discounts ?? [])?.reduce((acc: FormFinancialLineItem[], discount) => {
		if (!discount.amount) return acc;

		// Fixed rate amounts are major unit, i.e. 10 = $10
		// percentage amounts are as stored, i.e percentage rate of 10 = 10%
		let amount: CurrencyMinor | null = null;

		const baseLineItems = baseLineItemsData.filter(item => {
			const matchingType = discount.appliesTo.some(a => {
				if (a.targetId) {
					return a.targetType === item.targetType && a.targetId === item.targetId;
				} else {
					return a.targetType === item.targetType;
				}
			});
			return matchingType;
		});

		// If the discount doesn't apply to any line items do not apply discount
		if (baseLineItems.length === 0) return acc;

		const baseTotal = calculateLineItemsTotal(baseLineItems);

		if (discount.discountType === DiscountType.FixedRate) {
			amount = toCents(discount.amount as CurrencyMajor) as CurrencyMinor | null;
		} else if (discount.discountType === DiscountType.Percentage) {
			const percentage = discount.amount / 100;
			const percentageAmount = BrandMath.multiply(baseTotal, percentage);
			const percentageAmountRounded = roundNumber(percentageAmount, 0);
			amount = percentageAmountRounded > 0 ? percentageAmountRounded : null;
		}

		const finalDiscountAmount = amount && amount > baseTotal ? baseTotal : amount;

		if (finalDiscountAmount !== null) {
			acc.push({
				amount: -(finalDiscountAmount as number) as CurrencyMinor,
				comment: createDiscountComment(discount),
				immediatePayment: isImmediate,
				lineTypeId: StandardLineType.Discount
			});
		}

		return acc;
	}, []);

	return lineItems;
};

export const buildPaymentFeeLineItems = (
	paymentFees: PaymentBlockFee[],
	baseLineItemsDataImmediate: FormFinancialLineItem[],
	baseTotalCombined: CurrencyMajor
): FormFinancialLineItem[] => {
	const isImmediate = baseLineItemsDataImmediate?.length > 0;

	const lineItems: FormFinancialLineItem[] = paymentFees?.reduce((acc: FormFinancialLineItem[], fee) => {
		let amount: CurrencyMajor | null = null;
		let comment: string = fee.labelOverride ?? 'Fee';
		if (!fee.amount) return acc;

		if (fee.type === FeeType.FixedRate) {
			amount = (fee.amount ?? 0) as CurrencyMajor;
		} else if (fee.type === FeeType.Percentage) {
			const percentage = fee.amount / 100;
			const percentageAmount = BrandMath.multiply(baseTotalCombined, percentage);
			const percentageAmountRounded = roundNumber(percentageAmount, 2);
			amount = percentageAmountRounded > 0 ? percentageAmountRounded : null;
			comment = `${fee.labelOverride ?? 'Fee'} (${fee.amount}%)`;
		}
		if (amount !== null) {
			acc.push({
				amount: toCents(amount),
				comment,
				immediatePayment: isImmediate,
				lineTypeId: 1,
				invoice: fee.invoice
			});
		}

		return acc;
	}, []);

	return lineItems;
};

export const maskDetails = (value: string | number) => {
	const stringVal = String(value);
	return `${stringVal.substring(0, 2)}xxxx${stringVal.substring(stringVal.length - 2)} `;
};

/**
 * Calculates the amount of tax on a lineItem taking into account taxInclusive and the taxRate's rate
 * @param item - Line item
 * @param taxRate - Tax rate amount to use (the taxRate.rate)
 * @returns Tax amount number
 */
export const calculateItemTax = (item: FormFinancialLineItem, taxRate: number = 0) => {
	const tPercent = taxRate / 100;
	if (item.invoice?.taxInclusive === false) {
		return (item.amount * tPercent) as CurrencyMinor;
	} else {
		return ((item.amount * tPercent) / (1 + tPercent)) as CurrencyMinor;
	}
};

/**
 *  Calculcates the total tax amount for all lineItems.
 * @param lineItems
 * @param taxRates
 * @returns Tax rate total number
 */
export const calculateTaxTotal = (
	lineItems: FormFinancialLineItem[],
	taxRates?: {
		id: number;
		rate: number;
		default: boolean;
	}[]
) => {
	return lineItems.reduce((acc, cur) => {
		const taxRateId = cur.invoice?.taxRate;
		const defaultTaxRate = taxRates?.find(t => t.default);
		const matchingTaxRate = taxRates?.find(t => t.id === taxRateId);
		const taxRate = (matchingTaxRate ?? defaultTaxRate)?.rate;
		const taxAmount = BrandMath.round(calculateItemTax(cur, taxRate));
		return BrandMath.add(acc, taxAmount);
	}, ZERO_CENTS);
};
export const getPaymentAmounts = (
	sections: ISection[],
	answers: IQuestionAnswer[],
	method: PaymentMethod | undefined,
	fieldMeta: PaymentBlockMetadataType,
	questionName: IField['name'],
	layout: IWorkBenchLayout | undefined,
	taxRates?: TaxRatesData
) => {
	const allRequiredData = findRequiredData(answers, sections, fieldMeta.requires ?? [], layout);
	const baseLineItemsData = (allRequiredData['lineItems'] as FormFinancialLineItem[]).flat();
	const { lineItemsDataImmediate: baseLineItemsDataImmediate } = groupLineItemsByImmediacy(baseLineItemsData, method);

	const paymentFees = findPaymentFees(questionName, sections, answers);
	// TODO: this should be factoring in currency (not all minor units are / 100)
	const baseTotalMajor = toDollars(calculateLineItemsTotal(baseLineItemsData, taxRates?.rates));
	const discountLineItems = buildDiscountLineItems(questionName, answers, baseLineItemsDataImmediate, baseLineItemsData);
	const paymentFeeLineItems = buildPaymentFeeLineItems(paymentFees, baseLineItemsDataImmediate, baseTotalMajor);

	const { lineItemsDataImmediate, lineItemsDataNotImmediate } = groupLineItemsByImmediacy(
		[...baseLineItemsData, ...paymentFeeLineItems, ...discountLineItems],
		method
	);
	const totalPayNotImmediate = BrandMath.max(ZERO_CENTS, calculateLineItemsTotal(lineItemsDataNotImmediate, taxRates?.rates));
	const totalPayImmediate = BrandMath.max(ZERO_CENTS, calculateLineItemsTotal(lineItemsDataImmediate, taxRates?.rates));
	const grandTotal = BrandMath.max(ZERO_CENTS, BrandMath.add(totalPayImmediate, totalPayNotImmediate));

	return {
		grandTotal,
		totalPayNotImmediate,
		totalPayImmediate,
		lineItemsDataImmediate,
		lineItemsDataNotImmediate,
		lineItemCount: baseLineItemsData.length
	};
};
/**This also exists in snf, but that needs to have that code in snf (server side) */
export const validateDiscountCode = (discount: DiscountFragment, code: DiscountCodeModel, subs: string[], ownerId?: string): [boolean, string] => {
	const hasRemainingUses = code?.useLimit === null || (code?.useLimit ?? 0) > (code?.current ?? 0);
	const isEnabled = discount?.enabled && code?.enabled;
	const isValidOwner = discount.ownerType === 'PlanOption' || !discount.ownerId || ownerId === discount.ownerId;
	let isApplicableToSubs = false;

	// TODO - This logic will need to be updated when we support multiple appliesTo target types on one discount
	if (discount.appliesTo.some(appliesTo => appliesTo.targetType === AppliesToType.Subscription)) {
		isApplicableToSubs = discount.appliesTo.some(a => a.targetType === AppliesToType.Subscription && subs.includes(a.targetId));
	} else {
		isApplicableToSubs = true;
	}
	const isValid = isEnabled && hasRemainingUses && isValidOwner && isApplicableToSubs && isValidDate(discount?.validFrom, discount?.validTo);
	let reason: string = '';
	if (!isValid) {
		// Determine reason
		if (!hasRemainingUses) {
			reason = 'This discount code has been fully used up';
		} else if (!isEnabled) {
			reason = 'This discount code has been disabled';
		} else if (!isValidOwner || !isApplicableToSubs) {
			reason = 'This discount code is not applicable';
		}
	}
	return [isValid, reason];
};

const isValidDate = (validFrom?: string, validTo?: string): boolean => {
	const today = new Date();

	const fromDate = validFrom ? new Date(validFrom) : undefined;
	const toDate = validTo ? new Date(validTo) : undefined;

	const fromValid = !fromDate || fromDate <= today;
	const toValid = !toDate || toDate >= today;

	return fromValid && toValid;
};
